import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ApolloLink, ServerError, ServerParseError } from "@apollo/client";

import { csrfToken } from "utils/csrf";

// GraphQL requests authenticate with the same session cookie as the rest of the
// app (see credentials: "same-origin" on the HttpLink). A 401 therefore means
// the session is gone (signed out here or in another tab) so we reload and let
// Rails redirect to the login form.

const RELOADED_KEY = "pganalyze:reloadedAfterUnauthorized";

const withCsrfToken = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      "X-CSRF-Token": csrfToken(),
    },
  };
});

const isAuthError = (
  networkError: Error | ServerError | ServerParseError | undefined,
): boolean => {
  return (
    networkError != null &&
    networkError.name === "ServerError" &&
    "statusCode" in networkError && // https://github.com/apollographql/apollo-link/issues/300#issuecomment-518445337
    networkError.statusCode === 401
  );
};

const reloadWhenSignedOut = onError(({ networkError }) => {
  if (!isAuthError(networkError)) {
    return;
  }
  // Reload at most once per tab. If the server keeps rejecting requests that
  // the page load itself was allowed to make, reloading again would spin
  // forever.
  if (sessionStorage.getItem(RELOADED_KEY)) {
    return;
  }
  sessionStorage.setItem(RELOADED_KEY, "1");
  document.location.reload();
});

const clearReloadGuard = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    sessionStorage.removeItem(RELOADED_KEY);
    return result;
  }),
);

export const apolloAuthLink = withCsrfToken
  .concat(clearReloadGuard)
  .concat(reloadWhenSignedOut);
